import { appsignal } from '@lib/appsignal';
import { Roles } from '@lib/domain/auth';

export const hasRole = (role: Roles | string, roles?: string[]): boolean => {
  return roles?.includes(role) ?? false;
};

export const fetchAndExtractRoles = async (
  accessToken: string,
  darn_number: number,
  retries: number
): Promise<string[]> => {
  let url = '';
  try {
    // default to empty list of roles
    const roles: string[] = [];

    let rolesQry = '';
    Object.values(Roles).forEach((role, i) => {
      // if (role === Roles.SuperUser) return; // Just for debugging
      rolesQry += `${i === 0 ? '' : '&'}roles=${role}`;
    });

    url = `${process.env.NEXT_PUBLIC_GEO_API_BASE}/accessible-geographies/multiple-roles?${rolesQry}`;

    const structureResponse = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!structureResponse.ok) {
      const msg = await structureResponse.clone().text();
      throw msg;
    }

    const struct = await structureResponse.clone().json();
    // console.log('[STRUCTURES]', struct);

    for (const role of Object.values(Roles)) {
      if (!(role in struct)) continue;
      for (const s in Object.values(struct[role])) {
        if (s.length > 0) {
          roles.push(role);
          break;
        }
      }
    }

    // console.log('[ROLES]', roles);
    return roles;
  } catch (e) {
    if (retries > 0) {
      await sleep(300);
      appsignal.addBreadcrumb({
        category: 'Log',
        action: 'FETCH AND EXTRACT ROLES',
        metadata: {
          attempt: retries,
          error: String(e),
        },
      });
      return fetchAndExtractRoles(accessToken, darn_number, retries - 1);
    }
    appsignal.sendError(
      new Error(`Auth Error:fetchAndExtractRoles: ${e}`),
      span => {
        span.setAction('auth:fetchAndExtractRoles');
        span.setParams({
          route: url,
          error: e,
        });
        span.setTags({ user_darn: darn_number?.toString() });
      }
    );
    throw e;
  }
};

const sleep = (delay: number) =>
  new Promise(resolve => setTimeout(resolve, delay));

interface UserProfile {
  darn: number;
  name: string;
  position?: string;
  imageUrl?: string;
  biography?: string;
  location?: string;
}

export const fetchUserProfile = async (
  accessToken: string,
  darn_number: number
): Promise<UserProfile | null> => {
  const url = 'https://my.da.org.za/api/get-user-profile';
  try {
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!response.ok) {
      const msg = await response.clone().text();
      throw msg;
    }

    const profile = await response.clone().json();
    return profile;
  } catch (e) {
    appsignal.sendError(
      new Error(`Auth Error:fetchUserProfile: ${e}`),
      span => {
        span.setAction('auth:fetchUserProfile');
        span.setParams({
          route: url,
          error: e,
        });
        span.setTags({ user_darn: darn_number?.toString() });
      }
    );
    throw e;
  }
};
